import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  apiAdminGetWinLossByMaster,
  apiAdminExportWinLossByMaster,
  ApiRequestError,
} from '@/lib/api'
import type { MasterWinLossReport } from '@nexbit/types'
import { formatCurrency } from '@/lib/utils'
import { format, startOfMonth, endOfDay } from 'date-fns'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, Search, TrendingUp, ChevronRight, Download, Crown } from 'lucide-react'
import { useCan } from '@/hooks/usePermission'
import { cn } from '@/lib/utils'

function toDateInputValue(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

const signed = (v: string) => (parseFloat(v) >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold')

export default function AdminReportsPage() {
  const navigate = useNavigate()
  const [from, setFrom] = useState(toDateInputValue(startOfMonth(new Date())))
  const [to, setTo] = useState(toDateInputValue(endOfDay(new Date())))
  const [rows, setRows] = useState<MasterWinLossReport[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const can = useCan()
  const [applied, setApplied] = useState<{ from: string; to: string } | null>(null)

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true); setError(null)
    try {
      const range = { from: new Date(from).toISOString(), to: new Date(to + 'T23:59:59').toISOString() }
      setRows(await apiAdminGetWinLossByMaster(range))
      setApplied(range)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'โหลดรายงานไม่สำเร็จ')
    } finally { setLoading(false) }
  }

  async function handleExport() {
    if (!applied) return
    setExporting(true); setError(null)
    try { await apiAdminExportWinLossByMaster(applied) }
    catch (err) { setError(err instanceof ApiRequestError ? err.message : 'ส่งออกไม่สำเร็จ') }
    finally { setExporting(false) }
  }

  function drilldown(r: MasterWinLossReport) {
    if (!applied) return
    const q = new URLSearchParams({
      from: applied.from, to: applied.to,
      masterCode: r.masterCode, masterName: r.masterName,
    })
    navigate(`/admin/reports/master/${r.masterId ?? 'none'}?${q.toString()}`)
  }

  const totals = rows?.reduce((a, r) => ({
    bet: a.bet + parseFloat(r.totalBetAmount),
    win: a.win + parseFloat(r.totalWinAmount),
    profit: a.profit + parseFloat(r.netProfit),
    masterNet: a.masterNet + parseFloat(r.masterNet),
    adminNet: a.adminNet + parseFloat(r.adminNet),
    agents: a.agents + r.agentCount,
    rounds: a.rounds + r.roundCount,
    members: a.members + r.memberCount,
  }), { bet: 0, win: 0, profit: 0, masterNet: 0, adminNet: 0, agents: 0, rounds: 0, members: 0 })

  const inputClass = cn(
    'h-9 rounded-[var(--radius-sm)] border border-input bg-transparent px-3',
    'font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
  )

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="mb-5">
        <h1 className="text-lg font-semibold tracking-tight">รายงานกำไร/ขาดทุน</h1>
        <p className="text-sm text-muted-foreground mt-0.5">สรุปตามมาสเตอร์ — กดที่แถวเพื่อเจาะดูเอเจนต์ในสาย</p>
      </div>

      <form onSubmit={(e) => void handleSearch(e)}>
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)] mb-4">
          <div className="px-4 py-2.5 bg-secondary">
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">กรอง</span>
          </div>
          <div className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">ตั้งแต่</Label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required className={inputClass} />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">ถึง</Label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} required className={inputClass} />
            </div>
            <Button type="submit" size="sm" className="h-9 text-xs gap-1.5" disabled={loading}>
              <Search className="h-3.5 w-3.5" />ค้นหา
            </Button>
          </div>
        </div>
      </form>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden">
          <div className="px-4 py-2.5 bg-secondary"><Skeleton className="h-3 w-24" /></div>
          <div className="divide-y divide-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex gap-4 px-4 py-3">
                <Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
      ) : rows !== null ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
          <div className="px-4 py-2.5 bg-secondary flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5 text-secondary-foreground/60" />
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">ผลลัพธ์</span>
            <span className="font-mono text-xs text-secondary-foreground/60">{rows.length} มาสเตอร์</span>
            {rows.length > 0 && can('reports:export') && (
              <Button type="button" variant="outline" size="sm" onClick={() => void handleExport()} disabled={exporting} className="ml-auto h-7 text-xs gap-1.5">
                <Download className="h-3.5 w-3.5" />{exporting ? 'กำลังส่งออก...' : 'ส่งออก CSV'}
              </Button>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="py-12 text-center"><p className="text-sm text-muted-foreground">ไม่พบข้อมูลในช่วงที่เลือก</p></div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>มาสเตอร์</TableHead>
                    <TableHead className="text-right">เพดานหุ้น</TableHead>
                    <TableHead className="text-right">เอเจนต์</TableHead>
                    <TableHead className="text-right">ยอดเดิมพัน</TableHead>
                    <TableHead className="text-right">ยอดจ่าย</TableHead>
                    <TableHead className="text-right">กำไรสุทธิ</TableHead>
                    <TableHead className="text-right">ส่วนแบ่งมาสเตอร์</TableHead>
                    <TableHead className="text-right">ส่วนแบ่งแอดมิน</TableHead>
                    <TableHead className="text-right">รอบ</TableHead>
                    <TableHead className="text-right">สมาชิก</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.masterId ?? 'none'} className="cursor-pointer hover:bg-muted/50" onClick={() => drilldown(r)}>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {r.masterId && <Crown className="h-3.5 w-3.5 text-primary/60" />}
                          <span className="font-mono text-sm font-semibold">{r.masterCode}</span>
                          {/* Still counted in these totals — the tag says which part of them is test traffic. */}
                          {r.isStaging && (
                            <span className="rounded-full border border-warning/50 bg-warning/10 px-1.5 py-0.5 font-mono text-[9px] font-semibold text-warning">
                              STAGING
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {r.masterId ? `${(parseFloat(r.cap) * 100).toFixed(0)}%` : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">{r.agentCount}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(r.totalBetAmount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(r.totalWinAmount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm"><span className={signed(r.netProfit)}>{formatCurrency(r.netProfit)}</span></TableCell>
                      <TableCell className="text-right font-mono text-sm"><span className={signed(r.masterNet)}>{formatCurrency(r.masterNet)}</span></TableCell>
                      <TableCell className="text-right font-mono text-sm"><span className={signed(r.adminNet)}>{formatCurrency(r.adminNet)}</span></TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">{r.roundCount.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">{r.memberCount.toLocaleString()}</TableCell>
                      <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totals && (
                <div className="px-4 py-3 border-t border-border bg-secondary/20 flex flex-wrap gap-x-6 gap-y-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">ยอดรวม</span>
                  <span className="font-mono text-xs">ยอดเดิมพัน: <strong>{formatCurrency(totals.bet.toFixed(2))}</strong></span>
                  <span className="font-mono text-xs">ยอดจ่าย: <strong>{formatCurrency(totals.win.toFixed(2))}</strong></span>
                  <span className="font-mono text-xs">กำไรสุทธิ: <strong className={totals.profit >= 0 ? 'text-success' : 'text-destructive'}>{formatCurrency(totals.profit.toFixed(2))}</strong></span>
                  <span className="font-mono text-xs">ส่วนแบ่งมาสเตอร์: <strong className={totals.masterNet >= 0 ? 'text-success' : 'text-destructive'}>{formatCurrency(totals.masterNet.toFixed(2))}</strong></span>
                  <span className="font-mono text-xs">ส่วนแบ่งแอดมิน: <strong className={totals.adminNet >= 0 ? 'text-success' : 'text-destructive'}>{formatCurrency(totals.adminNet.toFixed(2))}</strong></span>
                  <span className="font-mono text-xs">รอบ: <strong>{totals.rounds.toLocaleString()}</strong></span>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-[var(--radius)] py-12 text-center">
          <p className="text-sm text-muted-foreground">เลือกช่วงวันที่แล้วกดค้นหาเพื่อดูรายงาน</p>
        </div>
      )}
    </div>
  )
}
